import {
  ChatBotResponse,
  ChatFormItem,
  ChatItemAnalyticsTag,
  ChatItemForm,
  ChatItemFormType,
  ChatItemSourceType,
  ChatItemType,
  ChatItem,
  dismissQuestion,
  sendAnswer,
  sendMessage,
  startChatBot,
  cancelChatBotRequests,
  ChatItemKBEntry,
  initChatBot,
} from 'app/services/chatBot';
import { Draft } from 'immer';
import { TopicSource } from 'app/copied/constants/common';

import { State } from './virtualAssistantStore';
import { ChatHandlerParams } from '../types/chat';
import { createUserMessage, createErrorMessage } from '../utils/chatItemUtils';

type SetState = (
  partial: Partial<State> | ((state: Draft<State>) => void) | ((state: State) => Partial<State>)
) => void;
type GetState = () => State;

export const createVirtualAssistantActions = (set: SetState, get: GetState) => ({
  cancelActiveRequests: (uuid: string) => {
    cancelChatBotRequests(uuid);
  },

  startVirtualAssistant: async (
    uuid: string,
    {
      action,
      isV41,
      isConvoAiEnabled,
      searchQuery,
    }: {
      action?: string;
      isV41: boolean;
      isConvoAiEnabled: boolean;
      searchQuery?: string;
    }
  ) => {
    const bypassConsent = !!searchQuery || !!action;
    const isConvAiActive = isConvoAiEnabled && isV41 && bypassConsent;

    if (get().hasStartedVirtualAssistant || get().isLoadingVirtualAssistant) {
      return;
    }

    get().cancelActiveRequests(uuid);

    set((state: Draft<State>) => {
      state.isLoadingVirtualAssistant = true;
      state.uuid = uuid;
      state.isConvAiActive = isConvAiActive;
    });

    try {
      const response = await startChatBot({ uuid, action, searchQuery });

      get().processResponse(response, { topicSource: action ? TopicSource.Pill : undefined });

      set((state: Draft<State>) => {
        state.hasStartedVirtualAssistant = true;
        state.isLoadingVirtualAssistant = false;
      });

      if (isV41) {
        set((state: Draft<State>) => {
          state.typingAreaAvailable = isConvAiActive;
          state.isV41 = true;
        });
      }
    } catch (error: unknown) {
      if (error instanceof Error && error.name !== 'AbortError' && error.name !== 'CanceledError') {
        get().presentError();
        set((state: Draft<State>) => {
          state.isLoadingVirtualAssistant = false;
        });
      }
    }
  },

  displayInitialMessage: (initialMessage: string) => {
    if (get().initialMessage) {
      return;
    }

    set((state: Draft<State>) => {
      state.userResponsePresent = true;
      state.chatItems.unshift(createUserMessage(initialMessage));
      state.initialMessage = initialMessage;
    });
  },

  sendAnswer: async (uuid: string, params: ChatHandlerParams, displayErrorMessage = false) => {
    get().cancelActiveRequests(uuid);

    set((state: Draft<State>) => {
      state.isTyping = true;
    });

    try {
      const isConvAiActive = get().isConvAiActive;
      if (params.formItem && params.chatItem) {
        get().trackAnswerSubmitted(params.formItem, params.answer, isConvAiActive, params.chatItem.aiResponse);
      }

      const response = await sendAnswer({ uuid, data: params.answer });

      let hasErrors = false;
      response?.form?.items?.forEach((item: ChatFormItem) => {
        if (item.errors && item.errors.length > 0) {
          hasErrors = true;
          get().trackAnswerSubmissionErrors(item);
        }
      });

      if (hasErrors) {
        set((state: Draft<State>) => {
          state.latestAnswerResponse = response;
        });
        return;
      }

      if (params.options?.createMessage && params.answer.value) {
        set((state: Draft<State>) => {
          state.chatItems.push(createUserMessage(params.answer.value as string, ChatItemSourceType.PATIENT_CHOICE));
        });
      }

      const isConsentResponse = Object.keys(response).includes('opt_in');
      const optOut = response?.opt_in === false;
      const pendingUserMessage = get().pendingUserMessage;

      if (!pendingUserMessage || optOut || !isConsentResponse) {
        get().processResponse(response, { topicSource: TopicSource.VAOptionClick });

        if (!isConsentResponse && pendingUserMessage) {
          set((state: Draft<State>) => {
            state.pendingUserMessage = undefined;
            state.hasCalledInit = false;
          });
          return;
        }
      }

      if (!optOut) {
        set((state: Draft<State>) => {
          state.userResponsePresent = true;
          if (isConsentResponse || params.options?.showTypingArea) {
            state.showTypingArea = true;
            state.isConvAiActive = true;
          }
        });

        if (pendingUserMessage) {
          get().sendMessage(uuid, pendingUserMessage);
          set((state: Draft<State>) => {
            state.pendingUserMessage = undefined;
          });
        }
      } else {
        set((state: Draft<State>) => {
          state.pendingUserMessage = undefined;
          state.showTypingArea = false;
          state.isConvAiActive = false;
        });
      }
    } catch (error: unknown) {
      if (
        error instanceof Error &&
        error.name !== 'AbortError' &&
        error.name !== 'CanceledError' &&
        displayErrorMessage
      ) {
        get().presentError();
      } else {
        set((state: Draft<State>) => {
          state.isTyping = false;
        });
      }
    }
  },

  sendMessage: async (uuid: string, message: string) => {
    get().cancelActiveRequests(uuid);

    if (!get().hasCalledInit && get().isV41) {
      const response = await initChatBot({ uuid });
      set((state: Draft<State>) => {
        state.hasCalledInit = true;
      });
      if (!response.opt_in) {
        set((state: Draft<State>) => {
          state.pendingUserMessage = message;
          state.chatItems = [];
          state.showTypingArea = false;
        });
        get().processResponse(response, { isInitResponse: true });
        return;
      }
    }

    set((state: Draft<State>) => {
      state.isTyping = true;
      state.chatItems.push(createUserMessage(message));
    });

    const isConvAiActive = get().isConvAiActive ?? false;
    get().trackQuerySubmitted({ query: message, isConvAiActive });

    try {
      const response = await sendMessage({ uuid, data: { content: message } });
      get().processResponse(response, { topicSource: TopicSource.ConvAiQuery, queryTyped: message });

      set((state: Draft<State>) => {
        state.userResponsePresent = true;
      });
    } catch (error: unknown) {
      if (error instanceof Error && error.name !== 'AbortError' && error.name !== 'CanceledError') {
        get().presentError();
      }
    }
  },

  dismissQuestion: async (uuid: string, params: ChatHandlerParams) => {
    get().cancelActiveRequests(uuid);

    set((state: Draft<State>) => {
      state.isTyping = true;

      if (params.options?.createMessage && params.answer.value) {
        state.chatItems.push(createUserMessage(params.answer.value as string));
      }

      if (params.options?.showTypingArea) {
        state.showTypingArea = true;
      }
    });

    const isConvAiActive = get().isConvAiActive;
    if (params.formItem && params.chatItem) {
      get().trackFormDismissed(params.formItem, isConvAiActive, params.chatItem.aiResponse);
    }

    try {
      const response = await dismissQuestion({ uuid, refId: params.answer.form_ref_id });
      get().processResponse(response);
    } catch (error: unknown) {
      if (error instanceof Error && error.name !== 'AbortError' && error.name !== 'CanceledError') {
        get().presentError();
      }
    }
  },

  presentError: () => {
    set((state: Draft<State>) => {
      state.isTyping = false;
      state.chatItems.push(createErrorMessage());
    });
  },

  processResponse: (
    response: ChatBotResponse,
    options: { topicSource?: TopicSource; queryTyped?: string; isInitResponse?: boolean } = {}
  ) => {
    set((state: Draft<State>) => {
      state.isTyping = true;
    });

    const filteredItems = get().filterNewChatItems(response, { isInitResponse: options.isInitResponse });
    const analyticsTagInfo = get().extractAnalyticsTagInfo(response);
    get().storeItemsTagInfo(analyticsTagInfo);

    // Add resultIndex to kb_entry items
    const itemsWithResultIndex = get().addResultIndexToKBEntries(filteredItems);

    set((state: Draft<State>) => {
      state.chatItems.push(
        ...itemsWithResultIndex.map((item) => {
          return {
            ...item,
            topicSource: options.topicSource,
            queryTyped: options.queryTyped,
          };
        })
      );
    });

    get().scheduleItemVisibility(itemsWithResultIndex);
    get().handleSpecialItemTypes(itemsWithResultIndex);

    if (options.isInitResponse && itemsWithResultIndex.length > 0) {
      set((state: Draft<State>) => {
        state.lastInitItemId = itemsWithResultIndex[itemsWithResultIndex.length - 1].uuid;
      });
    }
  },

  filterNewChatItems: (response: ChatBotResponse, options?: { isInitResponse?: boolean }): ChatItem[] => {
    const existingItems = get().chatItems;
    return response.chat_items
      .slice()
      .reverse()
      .filter((item) => {
        if (!options?.isInitResponse) {
          return item.source !== ChatItemSourceType.PATIENT;
        }
        return true;
      })
      .filter((item) => !existingItems.find((i) => i.uuid === item.uuid));
  },

  extractAnalyticsTagInfo: (
    response: ChatBotResponse
  ): { [key: string]: { tagId?: string; tagName?: string; posInResponse?: number } } => {
    const itemsTagInfo: { [key: string]: { tagId?: string; tagName?: string; posInResponse?: number } } = {};

    response.chat_items.forEach((item, i) => {
      if (item.type === ChatItemType.ANALYTICS_TAG) {
        const tagInfo = item as ChatItemAnalyticsTag;
        const nextMsg = response.chat_items.find((_item, _idx) => _idx > i && _item.type === ChatItemType.MESSAGE);
        if (nextMsg !== undefined) {
          itemsTagInfo[nextMsg.uuid] = {
            tagId: tagInfo.tag_id,
            tagName: tagInfo.tag_name,
            posInResponse: i,
          };
        }
      }
    });

    return itemsTagInfo;
  },

  handleSpecialItemTypes: (items: ChatItem[]): void => {
    items.forEach((item) => {
      switch (item.type) {
        case ChatItemType.ENABLE_TYPING_AREA:
          set((state: Draft<State>) => {
            state.typingAreaAvailable = true;
            state.isConvAiActive = true;
          });
          break;
        case ChatItemType.DISABLE_TYPING_AREA:
          set((state: Draft<State>) => {
            state.typingAreaAvailable = false;
            state.isConvAiActive = false;
          });
          break;
        case ChatItemType.FORM:
          if ((item as ChatItemForm).form_type === ChatItemFormType.CONTACT_FORM) {
            set((state: Draft<State>) => {
              state.showTypingArea = false;
            });
          }
          break;
      }
    });
  },

  scheduleItemVisibility: (items: ChatItem[]): void => {
    let totalDelay = 0;

    items.forEach((item, i) => {
      const delay = item.delay_duration || 0;

      set((state: Draft<State>) => {
        const newTimeout = setTimeout(
          () => {
            if (i === items.length - 1) {
              set((innerState: Draft<State>) => {
                innerState.isTyping = false;
              });
            }

            set((innerState: Draft<State>) => {
              innerState.chatItems.forEach((chatItem) => {
                if (chatItem.uuid === item.uuid) {
                  chatItem.visible = true;

                  get().trackItemVisibility(chatItem);
                }
              });
            });
          },
          (totalDelay += delay)
        );

        state.timeouts.push(newTimeout);
      });
    });
  },

  clearTimeouts: () => {
    get().timeouts.forEach((timeout: ReturnType<typeof setTimeout>) => clearTimeout(timeout));
    set((state: Draft<State>) => {
      state.timeouts = [];
    });
  },

  resetVirtualAssistant: () => {
    get().clearTimeouts();

    const currentUuid = get().uuid;
    if (currentUuid) {
      get().cancelActiveRequests(currentUuid);
    }

    set((state: Draft<State>) => {
      state.chatItems = [];
      state.hasStartedVirtualAssistant = false;
      state.isTyping = false;
      state.typingAreaAvailable = false;
      state.userResponsePresent = false;
      state.isLoadingVirtualAssistant = false;
      state.latestAnswerResponse = null;
      state.showTypingArea = true;
      state.itemsTagInfo = {};
      state.uuid = undefined;
      state.initialMessage = undefined;
      state.hasCalledInit = false;
      state.pendingUserMessage = undefined;
      state.lastInitItemId = undefined;
      state.isConvAiActive = false;
    });
  },

  setShowTypingArea: (showTypingArea: boolean) => {
    set((state: Draft<State>) => {
      state.showTypingArea = showTypingArea;
    });
  },

  resetScrollToBottomAfterInit: () => {
    set((state: Draft<State>) => {
      state.lastInitItemId = undefined;
    });
  },

  addResultIndexToKBEntries: (items: ChatItem[]): ChatItem[] => {
    const viewTypeIndexes: { [viewType: string]: number } = {};

    return items.map((item) => {
      if (item.type === ChatItemType.KB_ENTRY) {
        const kbItem = item as ChatItemKBEntry;
        const viewType = kbItem.view_type;

        if (viewTypeIndexes[viewType] === undefined) {
          viewTypeIndexes[viewType] = 0;
        }

        kbItem.resultIndex = viewTypeIndexes[viewType];

        viewTypeIndexes[viewType]++;

        return kbItem;
      }

      return item;
    });
  },
});
